import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { motion } from "framer-motion";
import { Sparkles, AlertTriangle } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { trackFP } from "../lib/firstPartyAnalytics";
import { newTrialSignupDestination } from "../lib/trialSignup";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MagicLinkVerify = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const token = params.get("token");
    if (!token) {
      setError("This link is missing its token. Please request a new one.");
      setTimeout(() => navigate("/login", { replace: true }), 2200);
      return;
    }

    (async () => {
      try {
        const { data } = await axios.post(
          `${API}/auth/magic-link/verify`,
          { token },
          { withCredentials: true }
        );
        setUser(data);
        // Returning magic-link logins are not signups and must not enter
        // welcome or acquisition automations.
        if (data?.is_new_signup) {
          try { trackFP("signup_completed", { method: "magic_link" }); } catch (_) { /* ignore */ }
        }
        // Honor campaign redirects (e.g. YC invite → /yc/claim). The
        // backend has already sanitized `next_path` to be an in-app
        // relative path; we re-check the leading `/` here as defense
        // in depth before handing it to react-router. Falls back to
        // /my-routines for normal sign-ins.
        const next = data?.next_path;
        const dest =
          typeof next === "string" && next.startsWith("/") && !next.startsWith("//")
            ? next
            : "/my-routines";
        navigate(
          data?.is_new_signup ? newTrialSignupDestination(dest) : dest,
          { replace: true, state: { user: data } }
        );
      } catch (e) {
        const msg =
          e?.response?.data?.detail ||
          "This link is no longer valid. Please request a new one.";
        setError(msg);
        setTimeout(() => navigate("/login", { replace: true }), 2600);
      }
    })();
  }, [params, navigate, setUser]);

  return (
    <div className="min-h-screen trubeaute-gradient flex items-center justify-center px-6">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center"
        data-testid="magic-link-verify-root"
      >
        <div
          className={`h-16 w-16 rounded-full flex items-center justify-center mx-auto mb-5 soft-shadow ${
            error
              ? "bg-[#F8EEEE]"
              : "bg-gradient-to-br from-[#D5A8A4] to-[#9B6A6C]"
          }`}
        >
          {error ? (
            <AlertTriangle className="h-7 w-7 text-[#9B6A6C]" />
          ) : (
            <Sparkles className="h-7 w-7 text-white" />
          )}
        </div>
        <h2 className="font-serif italic text-3xl text-[#2C2123] mb-2">
          {error ? "Link needs a refresh" : t("login.signingIn")}
        </h2>
        <p className="text-[#5C4B51] max-w-sm mx-auto">
          {error || "Just a moment — preparing your glow journal."}
        </p>
      </motion.div>
    </div>
  );
};

export default MagicLinkVerify;
